import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LangProvider } from './contexts/LangContext';
import { DataProvider } from './contexts/DataContext';

import LoginPage from './pages/auth/LoginPage';
import RedefinirSenhaPage from './pages/auth/RedefinirSenhaPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import Alunas from './pages/admin/Alunas';
import Aluna360 from './pages/admin/Aluna360';
import Treinos from './pages/admin/Treinos';
import CadastrarTreino from './pages/admin/CadastrarTreino';
import BibliotecaExercicios from './pages/admin/BibliotecaExercicios';
import BibliotecaTreinos from './pages/admin/BibliotecaTreinos';
import CadastrarDieta from './pages/admin/CadastrarDieta';
import CadastrarExame from './pages/admin/CadastrarExame';
import CadastrarPerfil from './pages/admin/CadastrarPerfil';
import Agendamentos from './pages/admin/Agendamentos';
import Checkins from './pages/admin/Checkins';
import Disponibilidade from './pages/admin/Disponibilidade';
import EvolucaoAluna from './pages/admin/EvolucaoAluna';
import Mensagens from './pages/admin/Mensagens';
import MeuPerfil from './pages/admin/MeuPerfil';
import Questionarios from './pages/admin/Questionarios';
import Configuracoes from './pages/admin/Configuracoes';
import AlunaDashboard from './pages/aluna/AlunaDashboard';
import HojeScreen from './pages/aluna/HojeScreen';
import AlunaAgendarAvaliacao from './pages/aluna/AlunaAgendarAvaliacao';
import AlunaPerfil from './pages/aluna/AlunaPerfil';
import AlunaEvolucao from './pages/aluna/AlunaEvolucao';
import AlunaChat from './pages/aluna/AlunaChat';
import AlunaQuestionario from './pages/aluna/AlunaQuestionario';
import AlunaQuestionarios from './pages/aluna/AlunaQuestionarios';
import AlunaJornada from './pages/aluna/AlunaJornada';
import IdPrototipoScreen from './pages/aluna/IdPrototipoScreen';
import InstallPrompt from './components/InstallPrompt';

function PrivateRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace state={{ from: `${location.pathname}${location.search}` }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { user } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const next = params.get('next');
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
  if (user) return <Navigate to={safeNext || (user.role === 'aluna' ? '/aluna/hoje' : '/admin')} replace />;
  return <LoginPage />;
}

function TestAccessRoute({ role }) {
  const { loginDemo, isLocalDev } = useAuth();
  const [done, setDone] = React.useState(false);
  const target = role === 'admin' ? '/admin' : '/aluna/hoje';

  React.useEffect(() => {
    let active = true;
    if (!isLocalDev) {
      setDone(true);
      return;
    }
    loginDemo(role).finally(() => {
      if (active) setDone(true);
    });
    return () => { active = false; };
  }, [isLocalDev, loginDemo, role]);

  if (!isLocalDev) return <Navigate to="/login" replace />;
  if (!done) return null;
  return <Navigate to={target} replace />;
}

function AlunaEntryRoute() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = params.get('tab');
  const tabRoutes = {
    treinos: '/aluna/treino',
    dieta: '/aluna/nutricao',
    mensagens: '/aluna/chat',
    historico: '/aluna/historico',
    avaliacao: '/aluna/agendar',
  };
  if (tabRoutes[tab]) return <Navigate to={tabRoutes[tab]} replace />;
  if (!tab) return <Navigate to="/aluna/hoje" replace />;
  return <PrivateRoute roles={['aluna']}><AlunaDashboard /></PrivateRoute>;
}

function PrototypeRoute() {
  const { isLocalDev, user } = useAuth();
  if (!isLocalDev) return <Navigate to={user?.role === 'admin' ? '/admin' : '/aluna/hoje'} replace />;
  return <PrivateRoute roles={['aluna', 'admin']}><IdPrototipoScreen /></PrivateRoute>;
}

function AdminAlunaPreviewRoute() {
  const { alunaId, treinoId } = useParams();
  return <PrivateRoute roles={['admin']}><AlunaDashboard initialTab="treinos" previewAlunaId={alunaId} previewTreinoId={treinoId || null} /></PrivateRoute>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return (
    <>
      <Routes>
        <Route path="/_preview/aluna" element={<TestAccessRoute role="aluna" />} />
        <Route path="/_preview/admin" element={<TestAccessRoute role="admin" />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
        <Route path="/admin" element={<PrivateRoute roles={['admin']}><AdminDashboard /></PrivateRoute>} />
        <Route path="/admin/alunas" element={<PrivateRoute roles={['admin']}><Alunas /></PrivateRoute>} />
        <Route path="/admin/alunas/:id" element={<PrivateRoute roles={['admin']}><Aluna360 /></PrivateRoute>} />
        <Route path="/admin/treinos" element={<PrivateRoute roles={['admin']}><Treinos /></PrivateRoute>} />
        <Route path="/admin/treino/novo" element={<PrivateRoute roles={['admin']}><CadastrarTreino /></PrivateRoute>} />
        <Route path="/admin/treino/:id" element={<PrivateRoute roles={['admin']}><CadastrarTreino /></PrivateRoute>} />
        <Route path="/admin/biblioteca-treinos" element={<PrivateRoute roles={['admin']}><BibliotecaTreinos /></PrivateRoute>} />
        <Route path="/admin/dieta/nova" element={<PrivateRoute roles={['admin']}><CadastrarDieta /></PrivateRoute>} />
        <Route path="/admin/dieta/:id" element={<PrivateRoute roles={['admin']}><CadastrarDieta /></PrivateRoute>} />
        <Route path="/admin/exame/novo" element={<PrivateRoute roles={['admin']}><CadastrarExame /></PrivateRoute>} />
        <Route path="/admin/exame/:id" element={<PrivateRoute roles={['admin']}><CadastrarExame /></PrivateRoute>} />
        <Route path="/admin/anamnese" element={<PrivateRoute roles={['admin']}><CadastrarPerfil /></PrivateRoute>} />
        <Route path="/admin/agendamentos" element={<PrivateRoute roles={['admin']}><Agendamentos /></PrivateRoute>} />
        <Route path="/admin/checkins" element={<PrivateRoute roles={['admin']}><Checkins /></PrivateRoute>} />
        <Route path="/admin/disponibilidade" element={<PrivateRoute roles={['admin']}><Disponibilidade /></PrivateRoute>} />
        <Route path="/admin/biblioteca" element={<PrivateRoute roles={['admin']}><BibliotecaExercicios /></PrivateRoute>} />
        <Route path="/admin/evolucao" element={<PrivateRoute roles={['admin']}><EvolucaoAluna /></PrivateRoute>} />
        <Route path="/admin/mensagens" element={<PrivateRoute roles={['admin']}><Mensagens /></PrivateRoute>} />
        <Route path="/admin/perfil" element={<PrivateRoute roles={['admin']}><MeuPerfil /></PrivateRoute>} />
        <Route path="/admin/questionarios" element={<PrivateRoute roles={['admin']}><Questionarios /></PrivateRoute>} />
        <Route path="/admin/configuracoes" element={<PrivateRoute roles={['admin']}><Configuracoes /></PrivateRoute>} />
        <Route path="/admin/preview/aluna/:alunaId" element={<AdminAlunaPreviewRoute />} />
        <Route path="/admin/preview/aluna/:alunaId/treino/:treinoId" element={<AdminAlunaPreviewRoute />} />
        <Route path="/aluna" element={<AlunaEntryRoute />} />
        <Route path="/aluna/hoje" element={<PrivateRoute roles={['aluna']}><HojeScreen /></PrivateRoute>} />
        <Route path="/aluna/treino" element={<PrivateRoute roles={['aluna']}><AlunaDashboard initialTab="treinos" /></PrivateRoute>} />
        <Route path="/aluna/nutricao" element={<PrivateRoute roles={['aluna']}><AlunaDashboard initialTab="dieta" /></PrivateRoute>} />
        <Route path="/aluna/chat" element={<PrivateRoute roles={['aluna']}><AlunaChat /></PrivateRoute>} />
        <Route path="/aluna/historico" element={<PrivateRoute roles={['aluna']}><AlunaDashboard initialTab="historico" /></PrivateRoute>} />
        <Route path="/aluna/jornada" element={<PrivateRoute roles={['aluna']}><AlunaJornada /></PrivateRoute>} />
        <Route path="/aluna/id-prototipo" element={<PrototypeRoute />} />
        <Route path="/aluna/id-prototipo/:tela" element={<PrototypeRoute />} />
        <Route path="/aluna/agendar" element={<PrivateRoute roles={['aluna']}><AlunaAgendarAvaliacao /></PrivateRoute>} />
        <Route path="/aluna/perfil" element={<PrivateRoute roles={['aluna']}><AlunaPerfil /></PrivateRoute>} />
        <Route path="/aluna/evolucao" element={<PrivateRoute roles={['aluna']}><AlunaEvolucao /></PrivateRoute>} />
        <Route path="/aluna/saude" element={<PrivateRoute roles={['aluna']}><AlunaQuestionarios /></PrivateRoute>} />
        <Route path="/aluna/questionarios" element={<PrivateRoute roles={['aluna']}><AlunaQuestionarios /></PrivateRoute>} />
        <Route path="/aluna/questionario" element={<Navigate to="/aluna/questionarios" replace />} />
        <Route path="/aluna/questionario/:tipo" element={<PrivateRoute roles={['aluna']}><AlunaQuestionario /></PrivateRoute>} />
        <Route path="/" element={<Navigate to={user ? (user.role === 'aluna' ? '/aluna' : '/admin') : '/login'} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      {user?.role === 'aluna' && <InstallPrompt />}
    </>
  );
}

export default function App() {
  return (
    <LangProvider>
    <AuthProvider>
      <DataProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </DataProvider>
    </AuthProvider>
    </LangProvider>
  );
}
